// JSX shared by the Shot Details and Pass Details side panels — ported from
// renderers-shotmap.js (renderDivergingBarChart + eventPitchPanelHtml). The row maths is
// the pure event-details-view.ts; this only paints it, with the legacy's exact classes.

import type { PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import type { DivergingRow } from "./event-details-view.js";
import { LIVE } from "../live/flag.js";
import { beginPanelDetach, panelLinkOf, usePanelSlot } from "../canvas/map-panels.js";
import { getDefaultSize } from "./module-config.js";
import { startMenuDrag } from "../canvas/menu-drag.js";

/** The zero-centred bar rows (xG / OBV). A missing value is a "—" row with no bar. */
export function DivergingBarChart({ rows }: { rows: DivergingRow[] }) {
  return (
    <div className="obv-chart">
      {rows.map((r) => (
        <div key={r.label} className={`obv-chart-row${r.isTotal ? " obv-chart-row-total" : ""}`}>
          <span className="obv-chart-label">{r.label}</span>
          <div className="obv-chart-track">
            <div className="obv-chart-zero"></div>
            {r.hasValue && <div className={`obv-chart-bar ${r.cls}`} style={{ left: `${r.leftPct}%`, width: `${r.widthPct}%` }}></div>}
          </div>
          <span className={r.hasValue ? `obv-chart-value ${r.cls}` : "obv-chart-value"}>{r.text}</span>
        </div>
      ))}
    </div>
  );
}

/** The pitch + side panel row. The header is omitted (not rendered empty) when absent, so
 *  the body takes the panel's full height. */
export function EventDetailLayout({
  pitch,
  header,
  children,
  mod,
  below,
}: {
  pitch: ReactNode;
  header?: string;
  children: ReactNode;
  mod?: HTMLElement;
  /** Above the panel, in its column, staying on the map when the panel comes out (a team map's
   *  players row). */
  below?: ReactNode;
}) {
  // ?live=1, a Shot Map / Pass Map: the panel drags out by its title as its own module (Ben,
  // 2026-10-31; canvas/map-panels.ts). Out, the pitch has the whole module and the panel draws here.
  const detachable = LIVE && !!mod && !!header;
  const link = mod ? panelLinkOf(mod) : null;
  const slot = usePanelSlot(link);
  const onHeaderPress = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!mod || window.innerWidth <= 768 || e.button !== 0) return;
    e.stopPropagation();
    const x0 = e.clientX;
    const y0 = e.clientY;
    const onMove = (ev: PointerEvent) => {
      if (Math.hypot(ev.clientX - x0, ev.clientY - y0) < 5) return;
      done();
      const { title, link: next } = beginPanelDetach(mod);
      const { cols, rows } = getDefaultSize(title);
      startMenuDrag(ev, title, cols, rows, { _panelLink: next });
    };
    const done = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", done);
      window.removeEventListener("pointercancel", done);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", done);
    window.addEventListener("pointercancel", done);
  };
  const panel = (
    <div className="event-details-panel">
      {header && (
        <div
          className={`event-details-header${detachable && !slot ? " event-details-header-draggable" : ""}`}
          onPointerDown={detachable && !slot ? onHeaderPress : undefined}
        >
          <span>
            {header}
            {slot && mod && <span className="event-details-link"> · {mapLabel(mod)}</span>}
          </span>
          {/* The cue that it comes out: an icon, no words (Ben, 2026-10-31: "No need to spell it out"). */}
          {detachable && !slot && (
            <svg className="event-details-detach" viewBox="0 0 16 16" aria-hidden="true">
              <rect x="2" y="5" width="9" height="9" rx="1.5" />
              <path d="M8 2h6v6M14 2 8.5 7.5" />
            </svg>
          )}
        </div>
      )}
      <div className="event-details-body">{children}</div>
    </div>
  );
  return (
    <div className={`event-detail-layout${slot ? " event-detail-layout-detached" : ""}`}>
      {pitch}
      {below ? (
        <div className={`event-detail-side${slot ? " event-detail-side-only" : ""}`}>
          {below}
          {slot ? createPortal(panel, slot) : panel}
        </div>
      ) : slot ? (
        createPortal(panel, slot)
      ) : (
        panel
      )}
    </div>
  );
}

/** "Pass Map · Declan Rice": which map a loose panel serves. */
function mapLabel(mod: HTMLElement): string {
  const title = mod.querySelector(".module-title")?.textContent?.trim() ?? "";
  const who = mod.querySelector<HTMLInputElement>(".player-search")?.value.trim();
  return who ? `${title} · ${who}` : title;
}

/** One label + value cell of the panel grid. */
export function DetailGroup({ label, span2, children }: { label: string; span2?: boolean; children: ReactNode }) {
  return (
    <div className={`event-detail-group${span2 ? " event-detail-span2" : ""}`}>
      <div className="event-detail-label">{label}</div>
      {children}
    </div>
  );
}
